import { type ILocationLGAInState, type ILocationState, type ILocationWardData } from '@interface/ILocation';
import type React from 'react';
import { useCallback, useEffect } from 'react';

import { ActionButton, AdvancedFiltersActions, AdvancedFiltersGrid, AdvancedFiltersPanel, FilterField } from './styled';
import { type ElectionFilters } from './useElectionFilters';

interface AdvancedFiltersProps {
	isOpen: boolean;
	filters: ElectionFilters;
	states: ILocationState[] | undefined;
	statesLoading: boolean;
	lgas: ILocationLGAInState[] | undefined;
	lgasLoading: boolean;
	wards: ILocationWardData[] | undefined;
	wardsLoading: boolean;
	onFilterChange: <K extends keyof ElectionFilters>(key: K, value: ElectionFilters[K]) => void;
	onStateChange: (stateId: number | null) => void;
	onLgaChange: (lgaId: number | null) => void;
	onReset: () => void;
	onFetchLgas: (stateId: number) => void;
	onFetchWards: (lgaId: number) => void;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
	isOpen,
	filters,
	states,
	statesLoading,
	lgas,
	lgasLoading,
	wards,
	wardsLoading,
	onFilterChange,
	onStateChange,
	onLgaChange,
	onReset,
	onFetchLgas,
	onFetchWards,
}) => {
	// Fetch LGAs when state changes
	useEffect(() => {
		if (filters.stateId) {
			onFetchLgas(filters.stateId);
		}
	}, [filters.stateId, onFetchLgas]);

	// Fetch wards when LGA changes
	useEffect(() => {
		if (filters.lgaId) {
			onFetchWards(filters.lgaId);
		}
	}, [filters.lgaId, onFetchWards]);

	const handleDateFromChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onFilterChange('dateFrom', e.target.value || null);
		},
		[onFilterChange]
	);

	const handleDateToChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onFilterChange('dateTo', e.target.value || null);
		},
		[onFilterChange]
	);

	const handleStateChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			onStateChange(value ? parseInt(value, 10) : null);
		},
		[onStateChange]
	);

	const handleLgaChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			onLgaChange(value ? parseInt(value, 10) : null);
		},
		[onLgaChange]
	);

	const handleWardChange = useCallback(
		(e: React.ChangeEvent<HTMLSelectElement>) => {
			const value = e.target.value;
			onFilterChange('wardId', value ? parseInt(value, 10) : null);
		},
		[onFilterChange]
	);

	const handleConstituencyChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onFilterChange('constituency', e.target.value);
		},
		[onFilterChange]
	);

	return (
		<AdvancedFiltersPanel
			className={isOpen ? 'open' : ''}
			aria-hidden={!isOpen}
		>
			<AdvancedFiltersGrid>
				<FilterField>
					<label htmlFor='filter-date-from'>Date From</label>
					<input
						type='date'
						id='filter-date-from'
						value={filters.dateFrom || ''}
						onChange={handleDateFromChange}
					/>
				</FilterField>

				<FilterField>
					<label htmlFor='filter-date-to'>Date To</label>
					<input
						type='date'
						id='filter-date-to'
						value={filters.dateTo || ''}
						onChange={handleDateToChange}
						min={filters.dateFrom || undefined}
					/>
				</FilterField>

				<FilterField>
					<label htmlFor='filter-state'>
						State
						{statesLoading && <span aria-label='Loading'> (loading...)</span>}
					</label>
					<select
						id='filter-state'
						value={filters.stateId || ''}
						onChange={handleStateChange}
						disabled={statesLoading}
					>
						<option value=''>All States</option>
						{states?.map((state) => (
							<option
								key={state.id}
								value={state.id}
							>
								{state.name}
							</option>
						))}
					</select>
				</FilterField>

				<FilterField>
					<label htmlFor='filter-lga'>
						LGA
						{lgasLoading && <span aria-label='Loading'> (loading...)</span>}
					</label>
					<select
						id='filter-lga'
						value={filters.lgaId || ''}
						onChange={handleLgaChange}
						disabled={!filters.stateId || lgasLoading}
					>
						<option value=''>{!filters.stateId ? 'Select a state first' : 'All LGAs'}</option>
						{lgas?.map((lga) => (
							<option
								key={lga.id}
								value={lga.id}
							>
								{lga.name}
							</option>
						))}
					</select>
				</FilterField>

				<FilterField>
					<label htmlFor='filter-ward'>
						Ward
						{wardsLoading && <span aria-label='Loading'> (loading...)</span>}
					</label>
					<select
						id='filter-ward'
						value={filters.wardId || ''}
						onChange={handleWardChange}
						disabled={!filters.lgaId || wardsLoading}
					>
						<option value=''>{!filters.lgaId ? 'Select an LGA first' : 'All Wards'}</option>
						{wards?.map((ward) => (
							<option
								key={ward.id}
								value={ward.id}
							>
								{ward.name}
							</option>
						))}
					</select>
				</FilterField>

				<FilterField>
					<label htmlFor='filter-constituency'>Constituency</label>
					<input
						type='text'
						id='filter-constituency'
						placeholder='Enter constituency...'
						value={filters.constituency}
						onChange={handleConstituencyChange}
					/>
				</FilterField>
			</AdvancedFiltersGrid>

			<AdvancedFiltersActions>
				<ActionButton
					type='button'
					onClick={onReset}
				>
					Reset
				</ActionButton>
			</AdvancedFiltersActions>
		</AdvancedFiltersPanel>
	);
};
